import {DatePicker} from '@ark-ui/react/date-picker';
import {Box, Flex, forwardRef, Table, Tbody, Td, Text, Th, Thead, Tr} from '@chakra-ui/react';
import {faChevronLeft} from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import {faChevronRight} from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ChakraDiv} from '@linx-security/ui/utils/chakra-elements';
import {IconButton} from '@linx-security/ui';
import type {RefAttributes} from 'react';

export const DatePickerContent = forwardRef<RefAttributes<HTMLDivElement>, typeof DatePicker.Content>((props, ref) => {
  return (
    <DatePicker.Content asChild ref={ref} {...props}>
      <Box
        sx={{
          backgroundColor: `white`,
          borderRadius: `md`,
          border: `1px solid`,
          padding: `8`,
          borderColor: `border`,
          zIndex: 1500,
        }}
      >
        <DatePicker.View view={`day`}>
          <DatePicker.Context>
            {api => (
              <>
                <DatePicker.ViewControl asChild>
                  <Flex justifyContent={`space-between`} alignItems={`middle`}>
                    <DatePicker.PrevTrigger asChild>
                      <IconButton
                        aria-label={`Next month`}
                        variant={`unstyled`}
                        icon={<FontAwesomeIcon icon={faChevronLeft} />}
                        sx={{
                          margin: `2`,
                          padding: `4`,
                          height: `auto`,
                          minHeight: `16`,
                          width: `auto`,
                          minWidth: `16`,
                          fontSize: `sm`,
                          _hover: {
                            backgroundColor: `blackAlpha.100`,
                          },
                        }}
                      />
                    </DatePicker.PrevTrigger>

                    <Text
                      as={ChakraDiv}
                      sx={{
                        fontSize: `sm`,
                        fontWeight: `semibold`,
                        display: `flex`,
                        alignItems: `center`,
                        cursor: `default`,
                      }}
                    >
                      <DatePicker.RangeText />
                    </Text>

                    <DatePicker.NextTrigger asChild>
                      <IconButton
                        aria-label={`Next month`}
                        variant={`unstyled`}
                        icon={<FontAwesomeIcon icon={faChevronRight} />}
                        sx={{
                          margin: `2`,
                          padding: `4`,
                          height: `auto`,
                          minHeight: `16`,
                          width: `auto`,
                          minWidth: `16`,
                          fontSize: `sm`,
                          _hover: {
                            backgroundColor: `blackAlpha.100`,
                          },
                        }}
                      />
                    </DatePicker.NextTrigger>
                  </Flex>
                </DatePicker.ViewControl>

                <DatePicker.Table asChild>
                  <Table
                    sx={{
                      maxWidth: `234px`,
                      position: `relative`,
                      marginTop: `8`,
                    }}
                  >
                    <DatePicker.TableHead asChild>
                      <Thead>
                        <DatePicker.TableRow asChild>
                          <Tr>
                            {api.weekDays.map((weekDay, id) => (
                              <DatePicker.TableHeader key={id} asChild>
                                <Th
                                  sx={{
                                    textAlign: `center`,
                                    border: 0,
                                    fontWeight: `semibold`,
                                    color: `td`,
                                    fontSize: `sm`,
                                  }}
                                >
                                  {weekDay.short.substring(0, 2)}
                                </Th>
                              </DatePicker.TableHeader>
                            ))}
                          </Tr>
                        </DatePicker.TableRow>
                      </Thead>
                    </DatePicker.TableHead>

                    <DatePicker.TableBody asChild>
                      <Tbody>
                        {api.weeks.map((week, id) => (
                          <DatePicker.TableRow key={id} asChild>
                            <Tr>
                              {week.map((day, id) => {
                                const disabled =
                                  api.visibleRange.start.compare(day) > 0 || api.visibleRange.end.compare(day) < 0;

                                const current = api.value?.[0] ? api.value[0].compare(day) === 0 : false;
                                return (
                                  <DatePicker.TableCell key={id} value={day} asChild>
                                    <Td sx={{textAlign: `center`, border: 0, padding: `2`}}>
                                      <DatePicker.TableCellTrigger style={{width: `100%`}} asChild>
                                        <Text
                                          sx={{
                                            paddingY: `4`,
                                            color: disabled ? `blackAlpha.400` : undefined,
                                            fontSize: `sm`,
                                            fontWeight: `semibold`,
                                            letterSpacing: `-1px`,
                                            backgroundColor: current ? `blackAlpha.300` : undefined,
                                            borderRadius: current ? `100%` : `sm`,
                                            cursor: disabled ? `default` : `pointer`,
                                            _hover: {
                                              backgroundColor: current || disabled ? undefined : `blackAlpha.100`,
                                            },
                                          }}
                                        >
                                          {day.day}
                                        </Text>
                                      </DatePicker.TableCellTrigger>
                                    </Td>
                                  </DatePicker.TableCell>
                                );
                              })}
                            </Tr>
                          </DatePicker.TableRow>
                        ))}
                      </Tbody>
                    </DatePicker.TableBody>
                  </Table>
                </DatePicker.Table>
              </>
            )}
          </DatePicker.Context>
        </DatePicker.View>
      </Box>
    </DatePicker.Content>
  );
});
